import { Injectable, Optional } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { CasService } from '../services/auth/cas-service';
import { Token } from '../models/token';
import { AuthService } from '../services/auth/auth-service';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardCAS implements CanActivate {
  constructor(
    private readonly router: Router,
    @Optional() private readonly cas: CasService
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    // If there is an explicit error in query params, attempt to re-initiate CAS login
    const error = route.queryParamMap.get('error');
    if (error) {
      // Do not call logout here to avoid competing navigations; just clear local tokens
      localStorage.removeItem(Token.tokenNameInStorage);
      localStorage.removeItem(Token.refreshTokenNameInStorage);
      if (this.cas) {
        this.cas.login(false, state.url);
        return false;
      }
      return this.router.parseUrl('/login?error=' + encodeURIComponent(error));
    }

    // If we don't have CAS available, allow navigation (avoid blocking the app in unexpected setups)
    if (!this.cas) {
      return true;
    }

    let jwt = localStorage.getItem(Token.tokenNameInStorage);

    // If we have an auth "code" in the URL and no JWT yet, wait for CAS service to exchange it
    const code = route.queryParamMap.get('code');
    if (!jwt && code) {
      try {
        // CasService constructor subscribes to query params and exchanges code automatically
        jwt = await firstValueFrom(
          this.cas.jwt$.pipe(
            filter((t) => !!t),
            take(1)
          )
        );
      } catch (e) {
        // If anything goes wrong, redirect to login preserving the target path
        this.cas.login(false, state.url);
        return false;
      }
    }

    // If still no JWT, trigger CAS login (will redirect away)
    if (!jwt) {
      this.cas.login(false, state.url);
      return false;
    }

    // Check that the user has the required role
    try {
      const isAllowed = await firstValueFrom(
        this.cas.isUserInRole(AuthService.neededRole).pipe(take(1))
      );
      if (isAllowed) {
        return true;
      }

      // No rights → trigger CAS login again
      this.cas.login(false, state.url);
      return false;
    } catch (e) {
      // On error, be safe and trigger CAS login again
      this.cas.login(false, state.url);
      return false;
    }
  }
}
